import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, roleLabel } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Bell, Moon, Sun, Search, LogOut, UserCircle, AlertTriangle, FileText, ClipboardCheck, BedDouble, Info, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { AIChatTrigger } from "@/components/AIChat";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PRIO_BORDER = {
  bassa: "border-l-slate-400",
  media: "border-l-blue-500",
  alta: "border-l-amber-500",
  critica: "border-l-rose-500",
};

function typeIcon(t) {
  if (t === "alert") return AlertTriangle;
  if (t === "audit_assegnato") return ClipboardCheck;
  if (t === "relazione_reminder") return FileText;
  if (t === "task_overdue") return BedDouble;
  return Info;
}

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
  return `${Math.floor(diff / 86400)} giorni fa`;
}

export default function Topbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  const initials = `${user?.nome?.[0] || ""}${user?.cognome?.[0] || ""}`.toUpperCase();

  const loadCount = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setCount(r.data?.count || 0);
    } catch {}
  };

  const loadNotifs = async () => {
    try {
      const r = await api.get("/notifications?limit=10");
      setNotifs(r.data || []);
    } catch {}
  };

  useEffect(() => {
    if (!user) return;
    loadCount();
    const id = setInterval(loadCount, 30000);
    return () => clearInterval(id);
  }, [user]);

  const onBellClick = async () => {
    if (!open) await loadNotifs();
    setOpen(!open);
  };

  const onNotifClick = async (n) => {
    try { await api.patch(`/notifications/${n.id}/leggi`); } catch {}
    setOpen(false);
    loadCount();
    loadNotifs();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.patch("/notifications/leggi-tutte");
    loadCount();
    loadNotifs();
  };

  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-30 h-[68px] ih-glass border-b border-border/50 flex items-center justify-between gap-4 pl-[60px] pr-4 md:px-6"
    >
      <div className="flex-1 max-w-xl">
        <GlobalSearch />
      </div>

      <div className="flex items-center gap-2">
        <button
          data-testid="theme-toggle"
          onClick={toggle}
          className="h-10 w-10 rounded-xl border border-border/60 hover:bg-accent/50 transition-colors flex items-center justify-center"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>

        <AIChatTrigger />

        <div className="relative">
          <button
            data-testid="notifications-btn"
            onClick={onBellClick}
            className="relative h-10 w-10 rounded-xl border border-border/60 hover:bg-accent/50 transition-colors flex items-center justify-center"
            aria-label="Notifiche"
          >
            <Bell className="h-[18px] w-[18px]" />
            {count > 0 && (
              <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">{count > 9 ? "9+" : count}</span>
            )}
          </button>

          {open && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
              <div className="absolute right-0 mt-2 w-[360px] max-h-[480px] overflow-hidden rounded-2xl ih-glass border border-border shadow-xl z-50" data-testid="notif-dropdown">
                <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
                  <span className="font-display text-sm tracking-tight">Notifiche</span>
                  {count > 0 && <button onClick={markAll} className="text-[11px] text-primary hover:underline">Segna tutte come lette</button>}
                </div>
                <div className="overflow-y-auto max-h-[360px]">
                  {notifs.length === 0 ? (
                    <div className="p-6 text-center text-xs text-muted-foreground">Nessuna notifica.</div>
                  ) : notifs.map((n) => {
                    const Icon = typeIcon(n.tipo);
                    return (
                      <button key={n.id} onClick={() => onNotifClick(n)} data-testid={`notif-${n.id}`} className={`w-full text-left px-4 py-3 hover:bg-accent/40 border-l-4 ${PRIO_BORDER[n.priorita] || "border-l-transparent"} ${!n.letto ? "bg-accent/20" : ""} border-b border-border/30`}>
                        <div className="flex items-start gap-2">
                          <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">{n.titolo}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{n.messaggio}</div>
                            <div className="text-[10px] text-muted-foreground/70 mt-0.5">{timeAgo(n.created_at)}</div>
                          </div>
                          {!n.letto && <span className="h-2 w-2 rounded-full bg-lavender-500 mt-1.5" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <button onClick={() => { setOpen(false); navigate("/notifiche"); }} className="w-full px-4 py-2.5 text-xs text-center border-t border-border/60 hover:bg-accent/40 text-muted-foreground">Vedi tutte le notifiche →</button>
              </div>
            </>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex items-center gap-2 h-10 pl-1 pr-3 rounded-xl border border-border/60 hover:bg-accent/40 transition-colors"
            >
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-lavender-300 to-lavender-500 flex items-center justify-center text-white text-xs font-semibold">
                {initials}
              </div>
              <div className="hidden sm:flex flex-col items-start leading-tight">
                <span className="text-[13px] font-medium">{user?.nome}</span>
                <span className="text-[10px] text-muted-foreground">{roleLabel(user?.role)}</span>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col">
                <span className="text-sm font-medium">{user?.nome} {user?.cognome}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-profilo" onClick={() => navigate("/profilo")}>
              <UserCircle className="h-4 w-4 mr-2" /> Profilo
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-guida" onClick={() => navigate("/guida")}>
              <BookOpen className="h-4 w-4 mr-2" /> Guida
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ hotels: [], users: [] });
  const [open, setOpen] = useState(false);
  const [cache, setCache] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (cache) return;
    Promise.all([
      api.get("/hotels").catch(() => ({ data: [] })),
      api.get("/users").catch(() => ({ data: [] })),
    ]).then(([h, u]) => setCache({ hotels: h.data || [], users: u.data || [] }));
  }, [cache]);

  useEffect(() => {
    if (!cache || !q.trim()) { setResults({ hotels: [], users: [] }); return; }
    const term = q.toLowerCase();
    setResults({
      hotels: cache.hotels.filter((h) => `${h.nome} ${h.citta}`.toLowerCase().includes(term)).slice(0, 5),
      users: cache.users.filter((u) => `${u.nome} ${u.cognome} ${u.email}`.toLowerCase().includes(term)).slice(0, 5),
    });
  }, [q, cache]);

  const totalRes = results.hotels.length + results.users.length;

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        data-testid="global-search"
        type="text"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Cerca strutture, utenti, ispezione…"
        className="w-full h-10 pl-9 pr-3 rounded-xl bg-background/60 border border-border/60 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-lavender-300 focus:bg-background"
      />
      {open && q.trim() && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute mt-2 w-full max-h-[400px] overflow-hidden rounded-2xl ih-glass border border-border shadow-xl z-40" data-testid="search-results">
            {totalRes === 0 ? <div className="p-4 text-xs text-muted-foreground">Nessun risultato per "{q}"</div> : (
              <div className="overflow-y-auto max-h-[400px]">
                {results.hotels.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Strutture</div>
                    {results.hotels.map((h) => (
                      <button key={h.id} onClick={() => { navigate(`/strutture/${h.id}`); setOpen(false); setQ(""); }} className="w-full text-left px-3 py-2 hover:bg-accent/40 text-sm">
                        <div className="font-medium">{h.nome}</div>
                        <div className="text-[11px] text-muted-foreground">{h.citta} · {h.numero_camere} camere</div>
                      </button>
                    ))}
                  </>
                )}
                {results.users.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 text-[9px] uppercase tracking-[0.18em] text-muted-foreground border-t border-border/40">Utenti</div>
                    {results.users.map((u) => (
                      <button key={u.id} onClick={() => { navigate("/utenti"); setOpen(false); setQ(""); }} className="w-full text-left px-3 py-2 hover:bg-accent/40 text-sm">
                        <div className="font-medium">{u.nome} {u.cognome}</div>
                        <div className="text-[11px] text-muted-foreground">{u.email} · {u.role}</div>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
